'use client';

/* eslint-disable @next/next/no-img-element */

import { useEffect, useMemo, useState } from 'react';
import { TbArrowUpRight as ArrowUpRight, TbPhoto as ImageIcon, TbPlayerPlay as Play } from 'react-icons/tb';
import { motion } from 'framer-motion';
import { FaInstagram } from 'react-icons/fa';
import { FiBatteryCharging, FiWifi } from 'react-icons/fi';
import { getMediaUrl } from '@/app/lib/media';
import type { Locale } from '@/i18n';

type GalleryProofItem = {
    id?: string | number;
    title?: string;
    type?: 'design' | 'video' | string;
    image?: any;
    videoUrl?: string | null;
    videoFile?: any;
    client?: string;
    description?: string;
};

type ProofAccount = {
    name: string;
    handle: string;
    url: string;
    tag: string;
    proof: string;
    services: string[];
};

type ProofCopy = {
    eyebrow: string;
    title: string;
    body: string;
    mediaLabel: string;
    visit: string;
    accountLabel: string;
    stripLabel: string;
    emptyMedia: string;
    accounts: ProofAccount[];
};

interface IndustryInstagramProofProps {
    slug: string;
    locale: Locale;
    galleryItems?: GalleryProofItem[];
}

const proofData: Record<'en' | 'ar', Record<string, ProofCopy>> = {
    en: {
        'food-beverage': {
            eyebrow: 'Instagram proof studio',
            title: 'Food brands people can judge before they visit.',
            body: 'For restaurants and cafes, Instagram is often the first table, menu, and mood board. This snapshot gives the sales team a fast way to show real accounts beside our visual work.',
            mediaLabel: 'Instagram preview',
            visit: 'Open Instagram',
            accountLabel: 'Food & beverage accounts',
            stripLabel: 'From our Work gallery',
            emptyMedia: 'Add more food reels and designs on the Work page to make this preview richer.',
            accounts: [
                {
                    name: 'Beit Omar',
                    handle: '@beit_omar',
                    url: 'https://www.instagram.com/beit_omar/',
                    tag: 'Restaurant presence',
                    proof: 'A warm account snapshot for owners who care about appetite, place, and daily trust.',
                    services: ['Food direction', 'Reels', 'Posting rhythm'],
                },
                {
                    name: 'Lavas',
                    handle: '@eat_lavash',
                    url: 'https://www.instagram.com/eat_lavash/',
                    tag: 'Casual dining',
                    proof: 'A direct example for brands that need the food to feel fast, fresh, and easy to order.',
                    services: ['Product shots', 'Short-form video', 'Campaign assets'],
                },
                {
                    name: 'Tawa',
                    handle: '@tawa.ksa',
                    url: 'https://www.instagram.com/tawa.ksa/',
                    tag: 'Saudi food brand',
                    proof: 'A useful reference for local restaurants that need stronger visual consistency.',
                    services: ['Brand consistency', 'Menu storytelling', 'Social layouts'],
                },
                {
                    name: 'Mawards',
                    handle: '@mawardsa',
                    url: 'https://www.instagram.com/mawardsa/',
                    tag: 'F&B account',
                    proof: 'A compact proof point for packaging, product moments, and repeatable feed systems.',
                    services: ['Feed system', 'Design templates', 'Launch content'],
                },
                {
                    name: 'Off White Lounge',
                    handle: '@offwhitelounge',
                    url: 'https://www.instagram.com/offwhitelounge/',
                    tag: 'Lounge experience',
                    proof: 'A stronger fit for experience-led venues where mood, lighting, and service cues sell.',
                    services: ['Atmosphere reels', 'Experience shots', 'Event content'],
                },
            ],
        },
        hospitality: {
            eyebrow: 'Instagram proof studio',
            title: 'Hospitality proof should feel premium before the booking.',
            body: 'Hotels, venues, and hospitality support brands need trust before the inquiry. This section turns selected accounts and production assets into a quick confidence builder.',
            mediaLabel: 'Instagram preview',
            visit: 'Open Instagram',
            accountLabel: 'Hospitality accounts',
            stripLabel: 'From our Work gallery',
            emptyMedia: 'Add more hospitality reels and designs on the Work page to make this preview richer.',
            accounts: [
                {
                    name: 'The Ritz-Carlton Riyadh',
                    handle: '@theritzcarltonruh',
                    url: 'https://www.instagram.com/theritzcarltonruh/',
                    tag: 'Luxury hotel',
                    proof: 'A premium reference for teams that need every post to protect the guest expectation.',
                    services: ['Guest journey', 'Venue visuals', 'Luxury tone'],
                },
                {
                    name: 'Super Clean Luxury Laundry',
                    handle: '@supercleanluxurylaundry',
                    url: 'https://www.instagram.com/supercleanluxurylaundry/',
                    tag: 'Hospitality support',
                    proof: 'A useful angle for service brands that support hotels, venues, and premium homes.',
                    services: ['Service proof', 'Before/after content', 'Trust visuals'],
                },
                {
                    name: 'Shahan',
                    handle: '@shahan.ksa',
                    url: 'https://www.instagram.com/shahan.ksa/',
                    tag: 'Hospitality brand',
                    proof: 'A flexible hospitality reference for presenting atmosphere, service, and brand recall.',
                    services: ['Visual identity', 'Social presence', 'Experience content'],
                },
            ],
        },
    },
    ar: {
        'food-beverage': {
            eyebrow: 'استوديو إثبات إنستغرام',
            title: 'حسابات مطاعم يقررها العميل قبل الزيارة.',
            body: 'في المطاعم والمقاهي يكون إنستغرام أول طاولة وقائمة وأجواء يراها العميل. هذا القسم يعطي فريق المبيعات لقطة سريعة تجمع الحسابات الحقيقية مع أعمالنا البصرية.',
            mediaLabel: 'معاينة إنستغرام',
            visit: 'افتح إنستغرام',
            accountLabel: 'حسابات الأغذية والمشروبات',
            stripLabel: 'من معرض أعمالنا',
            emptyMedia: 'أضف ريلز وتصاميم أكثر لقطاع المطاعم في صفحة الأعمال حتى تصبح اللقطة أقوى.',
            accounts: [
                {
                    name: 'بيت عمر',
                    handle: '@beit_omar',
                    url: 'https://www.instagram.com/beit_omar/',
                    tag: 'حضور مطعم',
                    proof: 'لقطة دافئة لصاحب مطعم يهتم بالشهية والمكان والثقة اليومية.',
                    services: ['توجيه الطعام', 'ريلز', 'إيقاع نشر'],
                },
                {
                    name: 'لافاس',
                    handle: '@eat_lavash',
                    url: 'https://www.instagram.com/eat_lavash/',
                    tag: 'مطعم كاجوال',
                    proof: 'مثال واضح للعلامات التي تحتاج أن يظهر الطعام سريعاً وطازجاً وسهل الطلب.',
                    services: ['تصوير منتجات', 'فيديو قصير', 'مواد حملات'],
                },
                {
                    name: 'تاوا',
                    handle: '@tawa.ksa',
                    url: 'https://www.instagram.com/tawa.ksa/',
                    tag: 'علامة طعام سعودية',
                    proof: 'مرجع مناسب للمطاعم المحلية التي تحتاج اتساقاً بصرياً أقوى.',
                    services: ['اتساق الهوية', 'سرد القائمة', 'قوالب اجتماعية'],
                },
                {
                    name: 'موارد',
                    handle: '@mawardsa',
                    url: 'https://www.instagram.com/mawardsa/',
                    tag: 'حساب أغذية ومشروبات',
                    proof: 'نقطة إثبات مختصرة للتغليف ولحظات المنتج وأنظمة النشر المتكررة.',
                    services: ['نظام محتوى', 'قوالب تصميم', 'محتوى إطلاق'],
                },
                {
                    name: 'أوف وايت لاونج',
                    handle: '@offwhitelounge',
                    url: 'https://www.instagram.com/offwhitelounge/',
                    tag: 'تجربة لاونج',
                    proof: 'يناسب الأماكن التي تبيع التجربة من خلال الأجواء والإضاءة وتفاصيل الخدمة.',
                    services: ['ريلز أجواء', 'تصوير تجربة', 'محتوى فعاليات'],
                },
            ],
        },
        hospitality: {
            eyebrow: 'استوديو إثبات إنستغرام',
            title: 'إثبات الضيافة يجب أن يشعر بالفخامة قبل الحجز.',
            body: 'الفنادق والوجهات وخدمات الضيافة تحتاج ثقة قبل الاستفسار. هذا القسم يحول الحسابات المختارة وأصول الإنتاج إلى لقطة تبني الاطمئنان بسرعة.',
            mediaLabel: 'معاينة إنستغرام',
            visit: 'افتح إنستغرام',
            accountLabel: 'حسابات الضيافة',
            stripLabel: 'من معرض أعمالنا',
            emptyMedia: 'أضف ريلز وتصاميم أكثر لقطاع الضيافة في صفحة الأعمال حتى تصبح اللقطة أقوى.',
            accounts: [
                {
                    name: 'ريتز كارلتون الرياض',
                    handle: '@theritzcarltonruh',
                    url: 'https://www.instagram.com/theritzcarltonruh/',
                    tag: 'فندق فاخر',
                    proof: 'مرجع فاخر للفرق التي تريد كل منشور أن يحافظ على توقع الضيف.',
                    services: ['رحلة الضيف', 'تصوير المكان', 'نبرة فاخرة'],
                },
                {
                    name: 'سوبر كلين لاكجري لوندري',
                    handle: '@supercleanluxurylaundry',
                    url: 'https://www.instagram.com/supercleanluxurylaundry/',
                    tag: 'خدمة داعمة للضيافة',
                    proof: 'زاوية مناسبة للعلامات الخدمية التي تدعم الفنادق والوجهات والمنازل الفاخرة.',
                    services: ['إثبات الخدمة', 'قبل وبعد', 'صور ثقة'],
                },
                {
                    name: 'شاهان',
                    handle: '@shahan.ksa',
                    url: 'https://www.instagram.com/shahan.ksa/',
                    tag: 'علامة ضيافة',
                    proof: 'مرجع مرن لعرض الأجواء والخدمة وتذكر العلامة.',
                    services: ['هوية بصرية', 'حضور اجتماعي', 'محتوى تجربة'],
                },
            ],
        },
    },
};

const mediaKeywords: Record<string, string[]> = {
    'food-beverage': [
        'food',
        'dining',
        'restaurant',
        'cafe',
        'coffee',
        'brew',
        'lumiere',
        'chef',
        'dish',
        'menu',
    ],
    hospitality: [
        'hotel',
        'hospitality',
        'resort',
        'luxury',
        'guest',
        'oasis',
        'venue',
        'room',
        'lounge',
        'service',
    ],
};

function getImageUrl(item?: GalleryProofItem) {
    return getMediaUrl(item?.image, 'card') || getMediaUrl(item?.image) || null;
}

function getHandleKey(handle: string) {
    return handle.replace(/^@/, '').toLowerCase();
}

function itemSearchText(item: GalleryProofItem) {
    return `${item.title || ''} ${item.client || ''} ${item.description || ''}`.toLowerCase();
}

function pickMediaForIndustry(items: GalleryProofItem[], slug: string) {
    const keywords = mediaKeywords[slug] || [];
    if (!items.length || !keywords.length) return items.slice(0, 8);

    const relevant = items.filter((item) => {
        const text = itemSearchText(item);
        return keywords.some((keyword) => text.includes(keyword));
    });

    const rest = items.filter((item) => !relevant.includes(item));
    return [...relevant, ...rest].slice(0, 8);
}

function getInstagramProfileEmbedUrl(profileUrl: string) {
    return `${profileUrl.replace(/\/?$/, '/')}embed/`;
}

function InstagramProfileEmbed({ account, compact = false }: { account: ProofAccount; compact?: boolean }) {
    return (
        <div
            className={`instagram-embed-shell overflow-hidden bg-white ${
                compact ? 'h-full min-h-[520px]' : 'min-h-[560px]'
            }`}
        >
            <iframe
                key={account.url}
                src={getInstagramProfileEmbedUrl(account.url)}
                title={`${account.name} Instagram profile`}
                className={`${compact ? 'h-[610px]' : 'h-[620px]'} w-full border-0 bg-white`}
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
            />
        </div>
    );
}

function getPhoneClockTime() {
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date());
}

function PhoneStatusBar({ isAr }: { isAr: boolean }) {
    const [time, setTime] = useState('9:41');

    useEffect(() => {
        const updateTime = () => setTime(getPhoneClockTime());

        updateTime();
        const interval = window.setInterval(updateTime, 60000);

        return () => window.clearInterval(interval);
    }, []);

    return (
        <div className="absolute inset-x-0 top-0 z-20 flex h-10 items-center justify-between px-5 pt-2 text-[10px] font-bold text-neutral-500">
            <span>{time}</span>
            <div className={`flex items-center gap-1.5 ${isAr ? 'flex-row-reverse' : ''}`}>
                <FiWifi className="h-3.5 w-3.5" />
                <FiBatteryCharging className="h-3.5 w-3.5" />
                <span className="max-w-[3.25rem] truncate text-[8px] font-bold uppercase tracking-[0.08em] text-neutral-500">
                    nibras
                </span>
            </div>
        </div>
    );
}

function FloatingInstagramPhone({
    account,
    avatarUrl,
    copy,
    isAr,
}: {
    account: ProofAccount;
    avatarUrl?: string | null;
    copy: ProofCopy;
    isAr: boolean;
}) {
    return (
        <div className="relative py-8 lg:py-10" style={{ perspective: '1500px' }}>
            <div
                className="relative mx-auto w-fit rounded-[2rem] shadow-[0_32px_90px_rgba(0,0,0,0.28)]"
                style={{
                    transformStyle: 'preserve-3d',
                }}
            >
                <motion.div
                    initial={{ transform: 'translateZ(10px) translateY(-3px)' }}
                    animate={{ transform: 'translateZ(34px) translateY(-10px)' }}
                    transition={{
                        repeat: Infinity,
                        repeatType: 'mirror',
                        duration: 2.7,
                        ease: 'easeInOut',
                    }}
                    className="relative h-[640px] w-[min(330px,calc(100vw-2rem))] rounded-[2rem] border-2 border-b-[7px] border-r-[7px] border-white/85 border-l-brand-text/35 border-t-brand-text/35 bg-neutral-950 p-2 shadow-[0_20px_52px_rgba(0,0,0,0.36)] md:h-[740px] md:w-[360px]"
                >
                    <div className="relative flex h-full flex-col overflow-hidden rounded-[1.55rem] bg-white">
                        <PhoneStatusBar isAr={isAr} />
                        <div className="absolute left-1/2 top-3 z-30 h-5 w-28 -translate-x-1/2 rounded-b-2xl rounded-t-md bg-neutral-950 shadow-inner" />

                        <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 pb-3 pt-11">
                            <div className="flex min-w-0 items-center gap-3">
                                <AccountAvatar account={account} avatarUrl={avatarUrl} size="sm" active />
                                <div className="min-w-0">
                                    <p className="truncate font-heading text-base font-bold leading-none text-brand-text">
                                        {account.name}
                                    </p>
                                    <p className="mt-1 truncate text-xs font-bold text-brand-text/50">{account.handle}</p>
                                </div>
                            </div>
                            <a
                                href={account.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-brand-text px-3 text-xs font-bold text-white transition-colors hover:bg-brand-cta"
                                aria-label={`${copy.visit}: ${account.handle}`}
                            >
                                {copy.visit}
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            </a>
                        </div>

                        <div className="relative min-h-0 flex-1 overflow-hidden bg-white">
                            <InstagramProfileEmbed account={account} compact />
                        </div>

                        <div className="border-t border-neutral-200 bg-white px-5 py-3">
                            <div className="mx-auto h-1.5 w-24 rounded-full bg-neutral-900" />
                        </div>
                    </div>
                </motion.div>
            </div>

            <div className="relative mx-auto mt-10 max-w-md rounded-[1.35rem] border border-brand-text/10 bg-white/80 p-4 shadow-[0_18px_54px_rgba(0,0,0,0.08)] backdrop-blur">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <span className="rounded-full bg-brand-cta/12 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-brand-cta">
                        {account.tag}
                    </span>
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-text/50">
                        <FaInstagram className="h-3.5 w-3.5" />
                        {account.handle}
                    </span>
                </div>
                <p className="text-sm leading-relaxed text-brand-text/72">{account.proof}</p>
            </div>
        </div>
    );
}

function AccountAvatar({
    account,
    avatarUrl,
    size = 'md',
    active = false,
}: {
    account: ProofAccount;
    avatarUrl?: string | null;
    size?: 'sm' | 'md';
    active?: boolean;
}) {
    const [failed, setFailed] = useState(false);
    const dimensions = size === 'sm' ? 'h-12 w-12' : 'h-10 w-10';
    const iconSize = size === 'sm' ? 'h-5 w-5' : 'h-5 w-5';
    const canShowImage = Boolean(avatarUrl) && !failed;

    useEffect(() => {
        setFailed(false);
    }, [avatarUrl]);

    return (
        <span
            className={`grid ${dimensions} shrink-0 place-items-center overflow-hidden rounded-full border ${
                active ? 'border-brand-cta/55 bg-brand-cta/10' : 'border-brand-text/12 bg-brand-text/8'
            }`}
            aria-hidden="true"
        >
            {canShowImage ? (
                <img
                    src={avatarUrl!}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setFailed(true)}
                />
            ) : (
                <FaInstagram className={`${iconSize} ${active ? 'text-brand-cta' : 'text-brand-text/50'}`} />
            )}
        </span>
    );
}

export default function IndustryInstagramProof({
    slug,
    locale,
    galleryItems = [],
}: IndustryInstagramProofProps) {
    const isAr = locale === 'ar';
    const copy = proofData[isAr ? 'ar' : 'en'][slug];
    const [activeIndex, setActiveIndex] = useState(0);
    const [avatars, setAvatars] = useState<Record<string, string | null>>({});

    const mediaItems = useMemo(() => pickMediaForIndustry(galleryItems, slug), [galleryItems, slug]);
    const accounts = useMemo(() => copy?.accounts || [], [copy]);

    useEffect(() => {
        if (!accounts.length) return;

        const controller = new AbortController();

        Promise.all(
            accounts.map(async (account) => {
                const handle = getHandleKey(account.handle);

                try {
                    const response = await fetch(`/api/instagram-avatar?handle=${encodeURIComponent(handle)}`, {
                        signal: controller.signal,
                    });
                    const data = await response.json();
                    return [handle, data.avatarUrl || null] as const;
                } catch {
                    return [handle, null] as const;
                }
            })
        ).then((entries) => {
            if (!controller.signal.aborted) {
                setAvatars(Object.fromEntries(entries));
            }
        });

        return () => controller.abort();
    }, [accounts]);

    if (!copy) return null;

    const activeAccount = copy.accounts[activeIndex % copy.accounts.length];
    const activeAvatar = avatars[getHandleKey(activeAccount.handle)];

    return (
        <section
            className="relative overflow-hidden bg-brand-bg px-4 py-24 text-brand-text md:px-20 md:py-32"
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,131,186,0.22),transparent_28rem),radial-gradient(circle_at_82%_42%,rgba(255,181,62,0.18),transparent_28rem),linear-gradient(180deg,rgba(255,255,255,0.03),transparent)]" />

            <div className="relative mx-auto max-w-7xl">
                <div className="mb-14 grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
                    <div>
                        <p className="mb-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.22em] text-brand-cta">
                            <FaInstagram className="h-5 w-5" />
                            {copy.eyebrow}
                        </p>
                        <h2 className="max-w-4xl font-heading text-5xl font-bold leading-none md:text-7xl">
                            {copy.title}
                        </h2>
                    </div>
                    <p className="max-w-2xl border-s-4 border-brand-cta ps-6 text-lg leading-relaxed text-brand-text/72 md:text-xl">
                        {copy.body}
                    </p>
                </div>

                <div className="grid gap-10 lg:grid-cols-[0.86fr_1fr] lg:items-start">
                    <div className="relative min-h-[760px] overflow-visible lg:min-h-[860px]">
                        <FloatingInstagramPhone
                            account={activeAccount}
                            avatarUrl={activeAvatar}
                            copy={copy}
                            isAr={isAr}
                        />
                    </div>

                    <div className="flex flex-col gap-5">
                        <div className="flex items-center justify-between gap-4">
                            <p className="font-heading text-sm font-bold uppercase tracking-[0.22em] text-brand-text/60">
                                {copy.accountLabel}
                            </p>
                            <a
                                href={activeAccount.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden items-center gap-2 rounded-full border border-brand-text/14 px-4 py-2 text-sm font-bold text-brand-text/70 transition-colors hover:border-brand-cta hover:text-brand-cta md:inline-flex"
                            >
                                {copy.visit}
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>

                        <div className="grid gap-3">
                            {copy.accounts.map((account, index) => {
                                const isActive = index === activeIndex;

                                return (
                                    <button
                                        key={account.handle}
                                        type="button"
                                        onClick={() => setActiveIndex(index)}
                                        className={`group relative overflow-hidden rounded-2xl border p-5 text-start transition-all duration-300 ${
                                            isActive
                                                ? 'border-brand-cta bg-brand-text text-brand-bg shadow-[0_18px_54px_rgba(234,50,40,0.22)]'
                                                : 'border-brand-text/12 bg-white/[0.03] text-brand-text hover:-translate-y-1 hover:border-brand-cta/55'
                                        }`}
                                    >
                                        <div className="absolute inset-y-0 end-0 w-28 bg-gradient-to-l from-brand-cta/18 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                            <div className="flex items-start gap-4">
                                                <AccountAvatar
                                                    account={account}
                                                    avatarUrl={avatars[getHandleKey(account.handle)]}
                                                    size="sm"
                                                    active={isActive}
                                                />
                                                <div>
                                                    <div className="mb-2 flex flex-wrap items-center gap-3">
                                                        <span className="font-heading text-2xl font-bold leading-none">
                                                            {account.name}
                                                        </span>
                                                        <span
                                                            className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                                                                isActive
                                                                    ? 'bg-brand-bg/12 text-brand-bg/72'
                                                                    : 'bg-brand-cta/12 text-brand-cta'
                                                            }`}
                                                        >
                                                            {account.tag}
                                                        </span>
                                                    </div>
                                                    <p className={isActive ? 'text-brand-bg/62' : 'text-brand-text/52'}>
                                                        {account.handle}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-2 md:max-w-[18rem] md:justify-end">
                                                {account.services.map((service) => (
                                                    <span
                                                        key={service}
                                                        className={`rounded-full border px-3 py-1 text-xs font-bold ${
                                                            isActive
                                                                ? 'border-brand-bg/16 text-brand-bg/70'
                                                                : 'border-brand-text/12 text-brand-text/58'
                                                        }`}
                                                    >
                                                        {service}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {mediaItems.length > 0 && (
                            <div className="mt-6">
                                <p className="mb-4 font-heading text-sm font-bold uppercase tracking-[0.22em] text-brand-text/60">
                                    {copy.stripLabel}
                                </p>
                                <div className="grid grid-cols-4 gap-2 md:grid-cols-8">
                                    {mediaItems.map((item, index) => {
                                        const thumb = getImageUrl(item);
                                        const isVideo = item.type === 'video';

                                        return (
                                            <button
                                                key={`${item.id || item.title}-${index}`}
                                                type="button"
                                                onClick={() => setActiveIndex(index % copy.accounts.length)}
                                                className="group relative aspect-square overflow-hidden rounded-xl border border-brand-text/10 bg-white/[0.04]"
                                            >
                                                {thumb ? (
                                                    <img
                                                        src={thumb}
                                                        alt={item.title || copy.mediaLabel}
                                                        className="h-full w-full object-cover opacity-75 transition-all duration-300 group-hover:scale-110 group-hover:opacity-100"
                                                    />
                                                ) : (
                                                    <div className="grid h-full place-items-center text-brand-text/25">
                                                        <ImageIcon className="h-5 w-5" />
                                                    </div>
                                                )}
                                                {isVideo && (
                                                    <span className="absolute inset-0 grid place-items-center bg-black/22 text-white">
                                                        <Play className="h-4 w-4 fill-white" />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
